import { Component, inject } from '@angular/core';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';

/**
 * Visual marker for cache-debug mode. Renders a diagonal-stripe overlay
 * plus a fixed top-right "CACHE DEBUG ON" pill so operators instantly
 * know the flag is live on whichever environment they're looking at.
 *
 * Included by `auth-layout` (pre-login) and `main-layout` (post-login)
 * so the effect is visible on every route. Emits nothing when
 * `RuntimeConfigService.cacheDebug()` is false — zero DOM, zero style
 * cost.
 */
@Component({
  selector: 'app-cache-debug-badge',
  standalone: true,
  template: `
    @if (cacheDebug()) {
      <div class="cache-debug-overlay" aria-hidden="true"></div>
      <div class="cache-debug-pill" role="status">CACHE DEBUG ON</div>
    }
  `,
  styles: [`
    /* Christmas-themed overlay: candy-cane red/white diagonal stripes + a
       soft evergreen vignette, with twinkling snow on top. Uses multiple
       stacked backgrounds so the effect is purely CSS — no extra DOM. */
    .cache-debug-overlay {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 2147483646;
      background:
        /* twinkling snow — two radial-gradient layers at different scales */
        radial-gradient(circle at 20% 30%, rgba(255,255,255,0.7) 0 1.5px, transparent 2px) 0 0/120px 120px,
        radial-gradient(circle at 70% 80%, rgba(255,255,255,0.55) 0 1px,   transparent 1.5px) 40px 60px/160px 160px,
        radial-gradient(circle at 50% 10%, rgba(255,255,255,0.5) 0 1px,   transparent 1.5px) 80px 20px/200px 200px,
        /* evergreen vignette — subtle pine-green edge */
        radial-gradient(ellipse at center, transparent 55%, rgba(13, 92, 47, 0.18) 100%),
        /* candy cane — red/white 45° stripes */
        repeating-linear-gradient(
          45deg,
          rgba(200, 16, 46, 0.18) 0 14px,
          rgba(255, 255, 255, 0.18) 14px 28px
        );
      animation: cache-debug-twinkle 4s ease-in-out infinite;
    }
    .cache-debug-pill {
      position: fixed;
      top: 8px;
      right: 8px;
      z-index: 2147483647;
      padding: 5px 12px 5px 22px;
      font: 700 11px/1 'Lato', system-ui, sans-serif;
      letter-spacing: 0.1em;
      color: #ffffff;
      /* red pill with white candy-cane stripes running through it */
      background:
        repeating-linear-gradient(
          45deg,
          #c8102e 0 6px,
          #ffffff 6px 10px,
          #c8102e 10px 16px
        );
      border: 1px solid #0d5c2f;
      border-radius: 999px;
      box-shadow:
        0 0 0 2px rgba(13, 92, 47, 0.35),
        0 2px 6px rgba(0, 0, 0, 0.25);
      text-shadow: 0 1px 0 rgba(0, 0, 0, 0.45);
      pointer-events: none;
    }
    /* A small tree glyph tucked inside the pill, left of the text. */
    .cache-debug-pill::before {
      content: '🎄';
      position: absolute;
      left: 6px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 12px;
      text-shadow: none;
    }
    @keyframes cache-debug-twinkle {
      0%, 100% { opacity: 0.75; }
      50%      { opacity: 1; }
    }
  `],
})
export class CacheDebugBadgeComponent {
  readonly cacheDebug = inject(RuntimeConfigService).cacheDebug;
}
